import { EMPTY, filter, Observable, switchMap, take, timeout } from 'rxjs'

/**
 * Runs `work` for each request once the document it targets has loaded, so that it never runs against another
 * document. A newer request replaces a pending one, and a request is dropped when a different document finishes
 * loading first.
 *
 * `loadedDocumentIds$` replays the document currently loaded and emits `null` while another one loads.
 */
export function onceRequestedDocumentLoads<Request extends { resourceId: number }, Result>(
  requests$: Observable<Request>,
  loadedDocumentIds$: Observable<number | null>,
  work: (request: Request) => Observable<Result>
): Observable<Result> {
  return requests$.pipe(
    switchMap((request) =>
      loadedDocumentIds$.pipe(
        filter((loadedDocumentId) => loadedDocumentId !== null),
        take(1),
        filter((loadedDocumentId) => loadedDocumentId === request.resourceId),
        switchMap(() => work(request))
      )
    )
  )
}

/**
 * The first element inside `container` that matches `selector` and is not one of `staleElements`, as soon as it has
 * rendered. Completes without emitting when none renders within `timeoutMs`.
 */
export function firstFreshElement(container: HTMLElement, selector: string, staleElements: ReadonlySet<Element>, timeoutMs: number): Observable<HTMLElement> {
  return new Observable<HTMLElement>((subscriber) => {
    const emitFreshElement = () => {
      const element = Array.from(container.querySelectorAll<HTMLElement>(selector)).find((candidate) => !staleElements.has(candidate)) ?? null

      if (element !== null) {
        subscriber.next(element)
        subscriber.complete()
      }
    }
    const observer = new MutationObserver(emitFreshElement)

    observer.observe(container, { childList: true, subtree: true })
    emitFreshElement()

    return () => observer.disconnect()
  }).pipe(timeout({ first: timeoutMs, with: () => EMPTY }))
}

/**
 * Scrolls `container`, and nothing around it, until `element` sits `marginPx` below its top edge. An element already
 * fully in view is left where it is.
 */
export function scrollContainerTo(container: HTMLElement, element: HTMLElement, marginPx: number) {
  const containerBounds = container.getBoundingClientRect()
  const elementBounds = element.getBoundingClientRect()

  if (elementBounds.top < containerBounds.top || elementBounds.bottom > containerBounds.bottom) {
    container.scrollBy({ top: elementBounds.top - containerBounds.top - marginPx })
  }
}
